//
// Copyright 2025 DXOS.org
//

// @import-as-namespace

import * as Schema from 'effect/Schema';

import { Annotation, DXN, Obj, Ref, Type } from '@dxos/echo';
import { GeneratorAnnotation, LabelAnnotation } from '@dxos/echo/Annotation';
import { Format } from '@dxos/echo/Format';
import { PropertyMeta } from '@dxos/echo/internal';

import * as Geo from './Geo';
import * as Organization from './Organization';

// TODO(burdon): Materialize link for Role (Organization => [Role] => Contact).
// TODO(burdon): Address sub type with geo location.
// TODO(burdon): Reconcile with user id.

/**
 * https://schema.org/Person
 * Based on fields from Apple Contacts.
 */
const PersonSchema = Schema.Struct({
  fullName: Schema.String.pipe(
    Schema.annotations({ title: 'Full Name' }),
    GeneratorAnnotation.set({
      generator: 'person.fullName',
      probability: 1,
    }),
    Schema.optional,
  ),
  preferredName: Schema.String.pipe(Schema.annotations({ title: 'Preferred Name' }), Schema.optional),
  nickname: Schema.String.pipe(Schema.annotations({ title: 'Nickname' }), Schema.optional),
  // TODO(wittjosiah): Format.URL. Support ref?
  image: Schema.String.pipe(
    Schema.annotations({ title: 'Image' }),
    GeneratorAnnotation.set('image.url'),
    Schema.optional,
  ),
  // TODO(burdon): Use reference links.
  organization: Ref.Ref(Organization.Organization).pipe(
    PropertyMeta('referenceProperty', 'name'),
    Schema.annotations({
      title: 'Employer',
      description: 'Current employer',
    }),
    Schema.optional,
  ),
  jobTitle: Schema.String.pipe(Schema.annotations({ title: 'Job Title' }), Schema.optional),
  department: Schema.String.pipe(Schema.annotations({ title: 'Department' }), Schema.optional),
  notes: Schema.String.pipe(Schema.annotations({ title: 'Notes' }), Schema.optional),
  // TODO(burdon): Change to array of `handles`.
  emails: Schema.Array(
    Schema.Struct({
      label: Schema.optional(Schema.String),
      value: Format.Email.pipe(
        Schema.annotations({ default: 'hello@email.com' }),
        GeneratorAnnotation.set('internet.email'),
      ),
    }),
  ).pipe(Schema.optional),
  identities: Schema.Array(
    Schema.Struct({
      label: Schema.optional(Schema.String),
      // TODO(burdon): Identity types? (socials, DIDs, etc.)
      value: Schema.String,
    }),
  ).pipe(Schema.optional),
  phoneNumbers: Schema.Array(
    Schema.Struct({
      label: Schema.optional(Schema.String),
      // TODO(wittjosiah): Format.Phone.
      value: Schema.String,
    }),
  )
    .pipe(Schema.optional)
    .annotations({ title: 'Phone Numbers' }),
  addresses: Schema.Array(
    Schema.Struct({
      label: Schema.optional(Schema.String),
      value: Geo.PostalAddress,
    }),
  ).pipe(Schema.optional),
  urls: Schema.Array(
    Schema.Struct({
      label: Schema.optional(Schema.String),
      value: Format.URL.pipe(
        Schema.annotations({ default: 'https://example.com' }),
        GeneratorAnnotation.set('internet.url'),
      ),
    }),
  ).pipe(Schema.optional),
  // TODO(burdon): Support date or create String type for ISO Date.
  birthday: Schema.String.pipe(
    Schema.annotations({ title: 'Birthday' }),
    GeneratorAnnotation.set('date.iso8601'),
    Schema.optional,
  ),
  // TODO(burdon): Move to base object?
  fields: Schema.Array(
    Schema.Struct({
      category: Schema.optional(Schema.String),
      label: Schema.String,
      value: Schema.String,
    }),
  ).pipe(Schema.optional),
});

const _PersonSchema = PersonSchema.pipe(
  Schema.extend(
    Schema.Struct({
      // TODO(wittjosiah): Reconcile with addresses.
      location: Format.GeoPoint.pipe(Schema.annotations({ title: 'Location' }), Schema.optional),
    }),
  ),
  Schema.annotations({ title: 'Person' }),
  LabelAnnotation.set(['preferredName', 'fullName', 'nickname']),
  Annotation.IconAnnotation.set({ icon: 'ph--user--regular', hue: 'neutral' }),
);

export class Person extends Type.makeObject<Person>(DXN.make('org.dxos.type.person', '0.1.0'))(_PersonSchema) {}

export const make = (props: Partial<Obj.MakeProps<typeof Person>> = {}) => Obj.make(Person, props);

const _LegacyPersonSchema = PersonSchema.pipe(
  Schema.annotations({ title: 'Person' }),
  LabelAnnotation.set(['preferredName', 'fullName', 'nickname']),
);

/**
 * @deprecated
 */
export const LegacyPerson = Type.makeObject(DXN.make('org.dxos.type.person', '0.1.0'))(_LegacyPersonSchema);
